import React, { useState, useEffect } from "react";
import simpleRenovate from "../../../assets/img/SimpleRenovate.svg";
import { useDispatch } from "react-redux";
import { hitSubmitContactUsApi } from "../../../Redux/Common/ContactUsSlice";
import { showToast } from "../../../Helper/Toast";
import BrowseIcon from "../../../assets/img/Browse icon.svg";
import { hitUploadMedia } from "../../../Redux/Common/UploadMediaSlice";
import { useGlobalState } from "../../../Providers/GlobalStateProvider";

const SUCCESS_MESSAGE =
  "Your message has been received. We will get back to you shortly.";

const ContactUsWeb = () => {
  const dispatch = useDispatch();
  const { setGlobalLoading } = useGlobalState();
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    attachments: null,
  });

  const [errors, setErrors] = useState({});
  const [fileName, setFileName] = useState("");

  useEffect(() => {
    setGlobalLoading(isSubmitting);
    return () => setGlobalLoading(false);
  }, [isSubmitting, setGlobalLoading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSubmitSuccess(false);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setSubmitSuccess(false);

    if (!selectedFile) {
      setFile(null);
      setFileName("");
      setFormData((prev) => ({
        ...prev,
        attachments: null,
      }));
      return;
    }

    setFile(selectedFile);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (isUploading) {
      showToast("Please wait for the file upload to finish", "warn");
      return;
    }

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      message: formData.message.trim(),
      attachments: formData.attachments,
    };

    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      const response = await dispatch(hitSubmitContactUsApi(payload)).unwrap();
      const successMessage = response?.message || SUCCESS_MESSAGE;

      setSubmitSuccess(true);
      showToast(successMessage, "success");
      setFormData({
        name: "",
        email: "",
        message: "",
        attachments: null,
      });
      setFile(null);
      setFileName("");
      setErrors({});
    } catch (error) {
      showToast(error?.message || "Submission failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!file) return undefined;

    let cancelled = false;

    const uploadFile = async () => {
      setIsUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append("myfile", file);

      try {
        const response = await dispatch(hitUploadMedia(uploadFormData)).unwrap();
        if (cancelled) return;

        const attachmentUrl = response?.data?.[0]?.url || "";

        if (!attachmentUrl) {
          throw new Error("File upload failed");
        }

        setFileName(file.name);
        setFormData((prev) => ({
          ...prev,
          attachments: attachmentUrl,
        }));
        showToast("File uploaded successfully", "success");
      } catch (error) {
        if (cancelled) return;

        setFile(null);
        setFileName("");
        setFormData((prev) => ({
          ...prev,
          attachments: null,
        }));
        showToast(error?.message || "File upload failed", "error");
      } finally {
        if (!cancelled) {
          setIsUploading(false);
        }
      }
    };

    uploadFile();

    return () => {
      cancelled = true;
    };
  }, [file, dispatch]);

  return (
    <div className="custom_container">
      <div className="contact-page">
        <div className="contact-page__header">
          <div className="logo">
            <img src={simpleRenovate} alt="SimpleRenovate" />
          </div>
          <h1 className="page-title">Contact our friendly team</h1>
          <p className="page-subtitle">Let us know how we can help</p>
        </div>

        <div className="contact-page__content">
          <div className="form-container">
            <h2 className="form-container__title">Contact us</h2>

            {submitSuccess && (
              <div className="contact-success-message" role="status">
                {SUCCESS_MESSAGE}
              </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={`form-input ${errors.name ? "error" : ""}`}
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <span className="error-message">{errors.name}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`form-input ${errors.email ? "error" : ""}`}
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  Message <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  className={`form-textarea ${errors.message ? "error" : ""}`}
                  placeholder="How can we help you?"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                {errors.message && (
                  <span className="error-message">{errors.message}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="attachments" className="form-label">
                  Attachment(s)
                </label>
                <div className="file-upload">
                  <input
                    type="file"
                    id="attachments"
                    name="attachments"
                    className="file-input"
                    onChange={handleFileChange}
                    disabled={isSubmitting || isUploading}
                  />
                  <label htmlFor="attachments" className="file-label">
                    <span className="file-icon">
                      <img src={BrowseIcon} alt="Browse icon" />
                    </span>
                    <span className="file-button">Browse</span>
                  </label>
                  {isUploading && (
                    <span className="file-upload-status">Uploading...</span>
                  )}
                  {fileName && !isUploading && (
                    <span className="file-name">{fileName}</span>
                  )}
                </div>
              </div>

              <div className="text-center text-md-start">
                <button
                  type="submit"
                  className="submit-button"
                  disabled={isSubmitting || isUploading}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsWeb;
